import React, { useState, cloneElement, useMemo, useRef, useCallback } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import {
    useFloating,
    useHover,
    useDismiss,
    useRole,
    useInteractions,
    FloatingPortal,
    offset,
    flip,
    shift,
    autoUpdate,
    arrow,
    useTransitionStyles,
} from '@floating-ui/react';

import { getOverrides, useClasses } from '../../../utils/overrides';
import { normalizeFloatingPlacement } from '../../../utils/placementMap';
import { createUseStyles } from '../../../utils/styles';
import styles from './styles';
const useStyles = createUseStyles(styles, 'Tooltip');

function assignRef(ref, node) {
    if (typeof ref === 'function') {
        ref(node);
        return;
    }

    if (ref) {
        ref.current = node;
    }
}

const ARROW_WIDTH = 4;
const HOVER_DELAY = { open: 0, close: 0 };
const TRANSITION_CONFIG = {
    duration: 200,
    initial: { opacity: 0 },
    open: { opacity: 1 },
    close: { opacity: 0 },
};

function Tooltip({
    children,
    classes: classesProp,
    overrides: overridesProp,
    className: classNameProp,
    content,
    placement = 'top',
    ...props
}) {
    const [internalOpen, setInternalOpen] = useState(false);
    const arrowRef = useRef(null);
    const classes = useClasses(useStyles, classesProp);

    // Overrides
    const override = getOverrides(overridesProp, Tooltip.overrides);

    const { visible: controlledVisible, onVisibleChange, ...restProps } = props;
    const isControlled = controlledVisible !== undefined;
    const isOpen = isControlled ? controlledVisible : internalOpen;

    // Handle visibility changes
    const handleOpenChange = (newOpen) => {
        if (isControlled) {
            onVisibleChange?.(newOpen);
        } else {
            setInternalOpen(newOpen);
            onVisibleChange?.(newOpen);
        }
    };

    const floatingPlacement = useMemo(() => normalizeFloatingPlacement(placement), [placement]);

    // Memoize middleware array to prevent recreating on every render
    const middleware = useMemo(
        () => [
            offset(ARROW_WIDTH * 2),
            flip(),
            shift({ padding: 8 }),
            arrow({ element: arrowRef }),
        ],
        [],
    );

    const { refs, floatingStyles, context, middlewareData } = useFloating({
        open: isOpen,
        onOpenChange: handleOpenChange,
        middleware,
        whileElementsMounted: autoUpdate,
        placement: floatingPlacement,
    });

    const { isMounted, styles: transitionStyles } = useTransitionStyles(context, TRANSITION_CONFIG);

    const hover = useHover(context, {
        delay: HOVER_DELAY,
    });

    const dismiss = useDismiss(context, {
        escapeKey: true,
    });

    const role = useRole(context, { role: 'tooltip' });

    const { getReferenceProps, getFloatingProps } = useInteractions([hover, dismiss, role]);

    const rootClassName = useMemo(() => {
        const actualPlacement = context.placement?.split('-')[0] || placement;
        return classnames(
            classes.root,
            classNameProp,
            `hoi-poi-tooltip-placement-${actualPlacement}`,
            override.root?.className,
        );
    }, [classes.root, classNameProp, context.placement, placement, override.root?.className]);

    const arrowStyles = useMemo(() => {
        const { x: arrowX, y: arrowY } = middlewareData.arrow || {};

        const staticSide = {
            top: 'bottom',
            right: 'left',
            bottom: 'top',
            left: 'right',
        }[context.placement.split('-')[0]];

        return {
            left: arrowX != null ? `${arrowX}px` : '',
            top: arrowY != null ? `${arrowY}px` : '',
            [staticSide]: `-${ARROW_WIDTH}px`,
        };
    }, [middlewareData.arrow, context.placement]);

    const hasValidTrigger = React.isValidElement(children);
    // Identity must stay stable. A new callback on each render makes React 19
    // detach the previous ref with null, and floating-ui's setReference then
    // setStates — which creates another callback and overflows the update depth.
    const mergedRefs = useRef([]);
    const mergedRef = useCallback((node) => {
        mergedRefs.current.forEach((ref) => assignRef(ref, node));
    }, []);

    let triggerElement = children || null;
    if (hasValidTrigger) {
        const referenceProps = getReferenceProps();
        const { ref: referencePropsRef, ...restReferenceProps } = referenceProps;
        mergedRefs.current = [children.props.ref, refs.setReference, referencePropsRef];
        triggerElement = cloneElement(children, {
            ...restReferenceProps,
            ref: mergedRef,
        });
    }

    // If no content, just return children without tooltip
    const hasContent = content !== undefined && content !== null;

    return (
        <>
            {triggerElement}

            {hasContent && isMounted && (
                <FloatingPortal>
                    <div
                        ref={refs.setFloating}
                        style={{ ...floatingStyles, ...transitionStyles }}
                        className={rootClassName}
                        {...restProps}
                        {...getFloatingProps()}
                        {...override.root}
                    >
                        <div className="hoi-poi-tooltip-inner">{content}</div>
                        <div ref={arrowRef} className="hoi-poi-tooltip-arrow" style={arrowStyles} />
                    </div>
                </FloatingPortal>
            )}
        </>
    );
}

Tooltip.overrides = ['root'];

Tooltip.propTypes = {
    /** Element that triggers the tooltip */
    children: PropTypes.node,
    /** Custom className for styling purposes */
    className: PropTypes.string,
    /** Object with custom style overrides for inner elements */
    overrides: PropTypes.object,
    /** Object with custom classes for inner elements */
    classes: PropTypes.object,
    /** The content to be displayed inside the tooltip (text, HTML, or React component) */
    content: PropTypes.any,
    /** Controls the position of the tooltip relative to the target element */
    placement: PropTypes.oneOf([
        'top', // Above the element, centered
        'topLeft', // Above the element, aligned to the left
        'topRight', // Above the element, aligned to the right
        'bottom', // Below the element, centered
        'bottomLeft', // Below the element, aligned to the left
        'bottomRight', // Below the element, aligned to the right
        'left', // To the left of the element, centered
        'leftTop', // To the left of the element, aligned to the top
        'leftBottom', // To the left of the element, aligned to the bottom
        'right', // To the right of the element, centered
        'rightTop', // To the right of the element, aligned to the top
        'rightBottom', // To the right of the element, aligned to the bottom
    ]),
    /** Controlled visibility state */
    visible: PropTypes.bool,
    /** Callback when visibility changes */
    onVisibleChange: PropTypes.func,
};

export default React.memo(Tooltip);
